/**
 * SlideSearchBar
 *
 * A dual-mode search bar:
 *   - **Keyword mode** (default): instant debounced text search + category filter
 *   - **AI mode**: natural language query sent to GPT for semantic search
 *
 * The user toggles between modes via a segmented control. The parent controls
 * state for both modes via props.
 */

import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { SlideCategory } from "@/features/slide-sense/types"

import { Loader2, Search, Send, Sparkles, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type SearchMode = "keyword" | "ai"

export interface SlideSearchBarProps {
  /** Current search mode */
  mode: SearchMode
  onModeChange: (mode: SearchMode) => void

  /** Keyword search state */
  query: string
  onQueryChange: (q: string) => void
  category: string | null
  onCategoryChange: (cat: string | null) => void

  /** AI search state */
  onAiSearch: (query: string) => void
  onAiClear?: () => void
  isAiSearching?: boolean
  hasAiResults?: boolean
  /**
   * True once an AI search has completed (even with zero results). Used to
   * keep the "Clear" affordance available so a no-results search isn't a
   * dead end — without it the user could only escape by switching modes.
   */
  hasAiSearched?: boolean

  className?: string
}

// ---------------------------------------------------------------------------
// Category options
// ---------------------------------------------------------------------------

const CATEGORY_OPTIONS: { value: SlideCategory; labelKey: string }[] = [
  { value: "title", labelKey: "slideSense.categoryTitle" },
  { value: "content", labelKey: "slideSense.categoryContent" },
  { value: "chart", labelKey: "slideSense.categoryChart" },
  { value: "table", labelKey: "slideSense.categoryTable" },
  { value: "image", labelKey: "slideSense.categoryImage" },
  { value: "divider", labelKey: "slideSense.categoryDivider" },
  { value: "closing", labelKey: "slideSense.categoryClosing" },
  { value: "other", labelKey: "slideSense.categoryOther" },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SlideSearchBar({
  mode,
  onModeChange,
  query,
  onQueryChange,
  category,
  onCategoryChange,
  onAiSearch,
  onAiClear,
  isAiSearching = false,
  hasAiResults = false,
  hasAiSearched = false,
  className,
}: SlideSearchBarProps) {
  const { t } = useTranslation()
  const [aiQuery, setAiQuery] = useState("")

  const handleAiSubmit = () => {
    if (aiQuery.trim() && !isAiSearching) {
      onAiSearch(aiQuery.trim())
    }
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {/* Mode toggle */}
        <div className="bg-muted/50 flex shrink-0 rounded-md border p-0.5" role="group">
          <button
            aria-pressed={mode === "keyword"}
            type="button"
            className={cn(
              "flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
              mode === "keyword"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => onModeChange("keyword")}
          >
            <Search className="h-3 w-3" />
            {t("slideSense.search")}
          </button>
          <button
            aria-pressed={mode === "ai"}
            type="button"
            className={cn(
              "flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
              mode === "ai"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => onModeChange("ai")}
          >
            <Sparkles className="h-3 w-3" />
            {t("slideSense.aiFind")}
          </button>
        </div>

        {mode === "keyword" ? (
          <>
            {/* Keyword search input */}
            <div className="relative flex-1">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                className="pr-8 pl-9"
                data-search-input="keyword"
                maxLength={500}
                placeholder={t("slideSense.searchPlaceholder")}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
              />
              {query.length > 0 && (
                <button
                  aria-label={t("slideSense.clearSearch")}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -mr-1 flex size-6 -translate-y-1/2 items-center justify-center rounded transition-colors"
                  type="button"
                  onClick={() => onQueryChange("")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Category filter */}
            <div className="w-36 shrink-0">
              <Select
                value={category ?? "all"}
                onValueChange={(val) => onCategoryChange(val === "all" ? null : val)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder={t("slideSense.allCategories")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("slideSense.allCategories")}</SelectItem>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear-all button */}
            {(query.length > 0 || category !== null) && (
              <Button
                className="text-muted-foreground shrink-0 text-xs"
                size="sm"
                variant="ghost"
                onClick={() => {
                  onQueryChange("")
                  onCategoryChange(null)
                }}
              >
                {t("slideSense.clear")}
              </Button>
            )}
          </>
        ) : (
          <>
            {/* AI search input */}
            <div className="relative flex-1">
              <Sparkles className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                className="pl-9"
                data-search-input="ai"
                disabled={isAiSearching}
                maxLength={500}
                placeholder={t("slideSense.aiSearchPlaceholder")}
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAiSubmit()
                }}
              />
            </div>

            {/* Submit button */}
            <Button
              className="shrink-0 gap-1.5"
              disabled={!aiQuery.trim() || isAiSearching}
              size="sm"
              onClick={handleAiSubmit}
            >
              {isAiSearching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              {isAiSearching ? t("slideSense.searching") : t("slideSense.find")}
            </Button>

            {/* Clear results button — also shown after a zero-result search so
                the user can return to browse without switching modes. */}
            {(hasAiResults || hasAiSearched) && !isAiSearching && (
              <Button
                className="text-muted-foreground shrink-0 text-xs"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAiQuery("")
                  onAiClear?.()
                }}
              >
                {t("slideSense.clear")}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
